import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './CreateListing.css';
import {
    ArrowLeft, X, AlertTriangle, Info, 
    Camera, CheckCircle, Shield
} from 'lucide-react';
import { useUser } from '../../../../UserContext';
import { clubsService, equipmentService, marketplaceService } from '../../../../services';

const CONDITIONS = ['New', 'Like New', 'Excellent', 'Good', 'Fair', 'Poor'];

const CreateListing = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useUser();
    const [isLoading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [manageableClubs, setManageableClubs] = useState([]);
    const [userEquipment, setUserEquipment] = useState([]);
    const [venueProducts, setVenueProducts] = useState([]);
    const [step, setStep] = useState(1);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showCertificateWarning, setShowCertificateWarning] = useState(false);
    const [listingSeedMode, setListingSeedMode] = useState('manual');
    const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
    const [selectedVenueProductId, setSelectedVenueProductId] = useState('');
    const [templateNotice, setTemplateNotice] = useState('');
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        categoryId: '',
        brandId: '',
        model: '',
        condition: 'Good',
        location: '',
        acceptsOffers: true,
        collectionOnly: false,
        requiresCertificate: false,
        certificateType: '',
        images: []
    });

    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [sellerMode, setSellerMode] = useState('member');
    const [selectedClubId, setSelectedClubId] = useState('');
    const preferredClubId = searchParams.get('clubId');
    const preferredSellerMode = searchParams.get('sellerMode');
    const preferredEquipmentId = searchParams.get('equipmentId');

    useEffect(() => {
        fetchCategories();
        fetchManageableClubs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchUserEquipment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.userId]);

    useEffect(() => {
        if (!preferredEquipmentId || userEquipment.length === 0) {
            return;
        }

        const selected = userEquipment.find((item) => String(item.equipmentId) === String(preferredEquipmentId));
        if (!selected) {
            return;
        }

        setListingSeedMode('equipment');
        setSelectedEquipmentId(String(selected.equipmentId));
    }, [preferredEquipmentId, userEquipment]);

    useEffect(() => {
        if (manageableClubs.length === 0) {
            setSellerMode('member');
            setSelectedClubId('');
            if (listingSeedMode === 'pos') {
                setListingSeedMode('manual');
            }
            return;
        }

        if (preferredSellerMode === 'venue') {
            setSellerMode('venue');
        }
    }, [listingSeedMode, manageableClubs.length, preferredSellerMode]);

    useEffect(() => {
        if (!preferredClubId || manageableClubs.length === 0) {
            return;
        }

        const isAllowedClub = manageableClubs.some((club) => String(club.clubId) === String(preferredClubId));
        if (isAllowedClub) {
            setSellerMode('venue');
            setSelectedClubId(String(preferredClubId));
        }
    }, [preferredClubId, manageableClubs]);

    useEffect(() => {
        if (sellerMode !== 'venue') {
            setSelectedClubId('');
            setVenueProducts([]);
            setSelectedVenueProductId('');
            if (listingSeedMode === 'pos') {
                setListingSeedMode('manual');
            }
            return;
        }

        if (!selectedClubId) {
            setVenueProducts([]);
            setSelectedVenueProductId('');
            return;
        }

        fetchVenueProducts(selectedClubId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClubId, sellerMode]);

    useEffect(() => {
        if (formData.categoryId) {
            fetchBrands(formData.categoryId);
        }
    }, [formData.categoryId]);

    useEffect(() => {
        if (formData.categoryId && categories.length > 0) {
            checkCertificateRequirement(formData.categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.categoryId, categories]);

    const fetchCategories = async () => {
        try {
            const data = await marketplaceService.getEquipmentCategories();
            setCategories(data);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const fetchBrands = async (categoryId) => {
        try {
            const data = await marketplaceService.getEquipmentBrands(categoryId);
            setBrands(data);
        } catch (err) {
            console.error('Error fetching brands:', err);
        }
    };

    const fetchManageableClubs = async () => {
        try {
            const params = new URLSearchParams();
            params.append('page', '1');
            params.append('pageSize', '100');
            params.append('sort', 'name_asc');
            const data = await clubsService.getManageClubs(params);
            setManageableClubs(data?.clubs || []);
        } catch (err) {
            console.error('Error fetching clubs for marketplace posting:', err);
            setManageableClubs([]);
        }
    };

    const fetchUserEquipment = async () => {
        try {
            const userId = user?.id || user?.userId;
            if (!userId) {
                setUserEquipment([]);
                return;
            }

            const data = await equipmentService.getUserEquipment(userId);
            setUserEquipment(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching user equipment for marketplace listing:', err);
            setUserEquipment([]);
        }
    };

    const fetchVenueProducts = async (clubId) => {
        try {
            const data = await clubsService.getClubTillProducts(clubId, false);
            setVenueProducts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching venue POS products for marketplace listing:', err);
            setVenueProducts([]);
        }
    };

    const checkCertificateRequirement = (categoryId) => {
        const selectedCategory = categories.find(c => c.categoryId === parseInt(categoryId));
        if (selectedCategory) {
            const name = selectedCategory.name.toLowerCase();
            const requiresCert = name.includes('shotgun') || name.includes('rifle') || 
                                name.includes('ammunition') || name.includes('cartridge');
            
            if (requiresCert) {
                setShowCertificateWarning(true);
                setFormData(prev => ({
                    ...prev,
                    requiresCertificate: true,
                    collectionOnly: true,
                    certificateType: name.includes('shotgun') || name.includes('cartridge') ? 'SGC' : 'FAC'
                }));
            } else {
                setShowCertificateWarning(false);
                setFormData(prev => ({
                    ...prev,
                    requiresCertificate: false,
                    certificateType: ''
                }));
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const applyEquipmentTemplate = useCallback(async (equipmentIdOverride = null) => {
        setTemplateNotice('');

        const effectiveEquipmentId = equipmentIdOverride ?? selectedEquipmentId;
        const selected = userEquipment.find((item) => String(item.equipmentId) === String(effectiveEquipmentId));
        if (!selected) {
            alert('Choose an equipment item first.');
            return;
        }

        let resolvedBrands = brands;
        if (selected.categoryId) {
            try {
                const brandResults = await marketplaceService.getEquipmentBrands(selected.categoryId);
                resolvedBrands = Array.isArray(brandResults) ? brandResults : [];
                setBrands(resolvedBrands);
            } catch (error) {
                console.error('Error fetching brands for equipment template:', error);
            }
        }

        const matchingBrand = resolvedBrands.find((brand) => String(brand.brandId) === String(selected.brandId));

        setFormData((prev) => ({
            ...prev,
            title: [selected.brandName, selected.model].filter(Boolean).join(' ').trim() || prev.title,
            description: selected.description || prev.description,
            price: selected.purchasePrice ? String(selected.purchasePrice) : prev.price,
            categoryId: selected.categoryId ? String(selected.categoryId) : prev.categoryId,
            brandId: matchingBrand ? String(matchingBrand.brandId) : '',
            model: selected.model || prev.model,
            condition: selected.condition || prev.condition
        }));

        setTemplateNotice('Equipment details added. Check the price, location, and description before publishing.');
    }, [brands, selectedEquipmentId, userEquipment]);

    useEffect(() => {
        if (!preferredEquipmentId || userEquipment.length === 0 || categories.length === 0) {
            return;
        }

        const selected = userEquipment.find((item) => String(item.equipmentId) === String(preferredEquipmentId));
        if (!selected) {
            return;
        }

        applyEquipmentTemplate(selected.equipmentId);
    }, [applyEquipmentTemplate, categories.length, preferredEquipmentId, userEquipment]);

    const useVenueProductTemplate = () => {
        const selected = venueProducts.find((item) => String(item.clubTillProductId) === String(selectedVenueProductId));
        if (!selected) {
            alert('Choose a venue POS product first.');
            return;
        }

        const matchingCategory = categories.find((category) =>
            (selected.category || '').toLowerCase().includes((category.name || '').toLowerCase()) ||
            (category.name || '').toLowerCase().includes((selected.category || '').toLowerCase()));

        setFormData((prev) => ({
            ...prev,
            title: selected.name || prev.title,
            description: selected.sku
                ? `${selected.name} (SKU: ${selected.sku})`
                : selected.name || prev.description,
            price: selected.unitPrice ? String(selected.unitPrice) : prev.price,
            categoryId: matchingCategory ? String(matchingCategory.categoryId) : prev.categoryId,
            model: selected.sku || prev.model,
            condition: 'New'
        }));
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + imageFiles.length > 8) {
            alert('Maximum 8 images allowed');
            return;
        }

        const newFiles = files.slice(0, 8 - imageFiles.length);
        setImageFiles(prev => [...prev, ...newFiles]);

        newFiles.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviews(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!agreedToTerms) {
            alert('Please agree to the terms and conditions');
            return;
        }

        if (sellerMode === 'venue' && !selectedClubId) {
            alert('Please choose the venue posting this listing.');
            return;
        }

        setIsLoading(true);

        try {
            const selectedCategory = categories.find((category) => category.categoryId === parseInt(formData.categoryId, 10));
            const selectedBrand = brands.find((brand) => brand.brandId === parseInt(formData.brandId, 10));

            const result = await marketplaceService.createListing({
                title: formData.title,
                description: formData.description,
                price: Number(formData.price),
                clubId: sellerMode === 'venue' ? Number(selectedClubId) : null,
                category: selectedCategory?.name || 'Accessories',
                firearmSection: formData.requiresCertificate
                    ? (formData.certificateType === 'FAC' ? 'Section1' : 'Section2')
                    : 'None',
                make: selectedBrand?.name || null,
                model: formData.model || null,
                condition: formData.condition || null,
                imageUrls: imagePreviews,
                location: formData.location || null,
                listingType: 'Sale'
            });
            navigate(`/dashboard/marketplace/${result.listingId}`);
        } catch (err) {
            alert(err.message || 'Failed to create listing');
        } finally {
            setIsLoading(false);
        }
    };

    const nextStep = () => {
        if (step === 1 && (!formData.title || !formData.categoryId || !formData.price)) {
            alert('Please fill in all required fields');
            return;
        }
        if (step === 1 && sellerMode === 'venue' && !selectedClubId) {
            alert('Please choose which venue is posting this listing.');
            return;
        }
        setStep(prev => prev + 1);
    };

    const prevStep = () => {
        setStep(prev => prev - 1);
    };

    return (
        <div className="create-listing-page">
            <button className="back-button" onClick={() => navigate('/dashboard/marketplace')}>
                <ArrowLeft size={20} />
                Back to Marketplace
            </button>

            <div className="create-listing-header">
                <h1>Create Listing</h1>
                <p>List your equipment for sale on Shoot Connect</p>
            </div>

            {/* Progress Steps */}
            <div className="progress-steps">
                <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                    <div className="step-number">{step > 1 ? <CheckCircle size={20} /> : '1'}</div>
                    <span>Details</span>
                </div>
                <div className="step-line"></div>
                <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                    <div className="step-number">{step > 2 ? <CheckCircle size={20} /> : '2'}</div>
                    <span>Photos</span>
                </div>
                <div className="step-line"></div>
                <div className={`step ${step >= 3 ? 'active' : ''}`}>
                    <div className="step-number">3</div>
                    <span>Review</span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="create-listing-form">
                {/* Step 1: Details */}
                {step === 1 && (
                    <div className="form-step">
                        <h2>Item Details</h2>

                        <div className="fee-info" style={{ marginBottom: '20px' }}>
                            <h4>Seller Profile</h4>
                            <p>
                                Post as yourself or, if you manage a venue, post on behalf of that venue.
                            </p>
                            <div className="form-options">
                                <label className="checkbox-label">
                                    <input
                                        type="radio"
                                        name="sellerMode"
                                        checked={sellerMode === 'member'}
                                        onChange={() => setSellerMode('member')}
                                    />
                                    <span>Post as member ({user?.userName || 'Your account'})</span>
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="radio"
                                        name="sellerMode"
                                        checked={sellerMode === 'venue'}
                                        onChange={() => setSellerMode('venue')}
                                        disabled={manageableClubs.length === 0}
                                    />
                                    <span>
                                        Post as venue
                                        {manageableClubs.length === 0 ? ' (no managed venues found)' : ''}
                                    </span>
                                </label>
                            </div>

                            {sellerMode === 'venue' && (
                                <div className="form-group" style={{ marginTop: '10px' }}>
                                    <label htmlFor="selectedClubId">Venue *</label>
                                    <select
                                        id="selectedClubId"
                                        value={selectedClubId}
                                        onChange={(event) => setSelectedClubId(event.target.value)}
                                        required
                                    >
                                        <option value="">Choose venue</option>
                                        {manageableClubs.map((club) => (
                                            <option key={club.clubId} value={club.clubId}>
                                                {club.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="fee-info" style={{ marginBottom: '20px' }}>
                            <h4>Quick Start (Optional)</h4>
                            <p>Start manually, or prefill from your equipment. If posting as a venue, you can prefill from venue POS products.</p>
                            {templateNotice && <p className="create-listing-template-notice">{templateNotice}</p>}
                            <div className="form-options">
                                <label className="checkbox-label">
                                    <input
                                        type="radio"
                                        name="listingSeedMode"
                                        checked={listingSeedMode === 'manual'}
                                        onChange={() => setListingSeedMode('manual')}
                                    />
                                    <span>Manual entry</span>
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="radio"
                                        name="listingSeedMode"
                                        checked={listingSeedMode === 'equipment'}
                                        onChange={() => setListingSeedMode('equipment')}
                                    />
                                    <span>Use my equipment</span>
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="radio"
                                        name="listingSeedMode"
                                        checked={listingSeedMode === 'pos'}
                                        onChange={() => setListingSeedMode('pos')}
                                        disabled={sellerMode !== 'venue' || !selectedClubId}
                                    />
                                    <span>Use venue POS product</span>
                                </label>
                            </div>

                            {listingSeedMode === 'equipment' && (
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="selectedEquipmentId">My equipment</label>
                                        <select
                                            id="selectedEquipmentId"
                                            value={selectedEquipmentId}
                                            onChange={(event) => setSelectedEquipmentId(event.target.value)}
                                        >
                                            <option value="">Choose equipment</option>
                                            {userEquipment.map((item) => (
                                                <option key={item.equipmentId} value={item.equipmentId}>
                                                    {[item.brandName, item.model].filter(Boolean).join(' ')} ({item.categoryName || 'Equipment'})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ alignSelf: 'end' }}>
                                        <button type="button" className="btn-secondary" onClick={applyEquipmentTemplate}>
                                            Use equipment details
                                        </button>
                                    </div>
                                </div>
                            )}

                            {listingSeedMode === 'pos' && (
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="selectedVenueProductId">Venue POS product</label>
                                        <select
                                            id="selectedVenueProductId"
                                            value={selectedVenueProductId}
                                            onChange={(event) => setSelectedVenueProductId(event.target.value)}
                                            disabled={sellerMode !== 'venue' || !selectedClubId}
                                        >
                                            <option value="">Choose POS product</option>
                                            {venueProducts.map((item) => (
                                                <option key={item.clubTillProductId} value={item.clubTillProductId}>
                                                    {item.name} ({item.category || 'General'}) - £{Number(item.unitPrice || 0).toFixed(2)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ alignSelf: 'end' }}>
                                        <button type="button" className="btn-secondary" onClick={useVenueProductTemplate}>
                                            Use POS details
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {showCertificateWarning && (
                            <div className="certificate-warning">
                                <AlertTriangle size={24} />
                                <div>
                                    <h3>Firearm/Ammunition Listing</h3>
                                    <p>
                                        This category requires buyers to hold a valid {formData.certificateType === 'FAC' ? 'Firearms Certificate' : 'Shotgun Certificate'}.
                                        Collection only - no shipping. You must verify the buyer's certificate before transfer.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="title">Title *</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="e.g., Beretta 686 Silver Pigeon I"
                                maxLength={100}
                                required
                            />
                            <span className="char-count">{formData.title.length}/100</span>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="categoryId">Category *</label>
                                <select
                                    id="categoryId"
                                    name="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select category</option>
                                    {categories.map(cat => (
                                        <option key={cat.categoryId} value={cat.categoryId}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="brandId">Brand</label>
                                <select
                                    id="brandId"
                                    name="brandId"
                                    value={formData.brandId}
                                    onChange={handleInputChange}
                                    disabled={!formData.categoryId}
                                >
                                    <option value="">Select brand</option>
                                    {brands.map(brand => (
                                        <option key={brand.brandId} value={brand.brandId}>
                                            {brand.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="model">Model</label>
                                <input
                                    type="text"
                                    id="model"
                                    name="model"
                                    value={formData.model}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Silver Pigeon I"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="condition">Condition *</label>
                                <select
                                    id="condition"
                                    name="condition"
                                    value={formData.condition}
                                    onChange={handleInputChange}
                                    required
                                >
                                    {CONDITIONS.map(cond => (
                                        <option key={cond} value={cond}>{cond}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">Description *</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Describe your item, including any notable features, history, or included accessories..."
                                rows={6}
                                required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="price">Price (£) *</label>
                                <input
                                    type="number"
                                    id="price"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    placeholder="0.00"
                                    min="1"
                                    step="0.01"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="location">Location *</label>
                                <input
                                    type="text"
                                    id="location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Yorkshire, UK"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-options">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="acceptsOffers"
                                    checked={formData.acceptsOffers}
                                    onChange={handleInputChange}
                                />
                                <span>Accept offers from buyers</span>
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="collectionOnly"
                                    checked={formData.collectionOnly}
                                    onChange={handleInputChange}
                                    disabled={formData.requiresCertificate}
                                />
                                <span>Collection only (no shipping)</span>
                                {formData.requiresCertificate && (
                                    <span className="required-note">(Required for firearms/ammunition)</span>
                                )}
                            </label>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard/marketplace')}>
                                Cancel
                            </button>
                            <button type="button" className="btn-primary" onClick={nextStep}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Photos */}
                {step === 2 && (
                    <div className="form-step">
                        <h2>Add Photos</h2>
                        <p className="step-description">Add up to 8 photos. The first photo will be your listing's main image.</p>

                        <div className="image-upload-area">
                            {imagePreviews.map((preview, index) => (
                                <div key={index} className={`image-preview ${index === 0 ? 'main' : ''}`}>
                                    <img src={preview} alt={`Preview ${index + 1}`} />
                                    <button 
                                        type="button" 
                                        className="remove-image"
                                        onClick={() => removeImage(index)}
                                    >
                                        <X size={16} />
                                    </button>
                                    {index === 0 && <span className="main-badge">Main</span>}
                                </div>
                            ))}
                            
                            {imagePreviews.length < 8 && (
                                <label className="upload-button">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                        hidden
                                    />
                                    <Camera size={32} />
                                    <span>Add Photos</span>
                                    <span className="upload-count">{imagePreviews.length}/8</span>
                                </label>
                            )}
                        </div>

                        <div className="photo-tips">
                            <h4><Info size={16} /> Photo Tips</h4>
                            <ul>
                                <li>Use good lighting and a clean background</li>
                                <li>Show the item from multiple angles</li>
                                <li>Include close-ups of any wear, damage, or notable features</li>
                                <li>For firearms, include the serial number area (you can blur the number)</li>
                            </ul>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondary" onClick={prevStep}>
                                Back
                            </button>
                            <button type="button" className="btn-primary" onClick={nextStep}>
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Review */}
                {step === 3 && (
                    <div className="form-step">
                        <h2>Review Your Listing</h2>

                        <div className="review-section">
                            <div className="review-images">
                                {imagePreviews.length > 0 ? (
                                    <img src={imagePreviews[0]} alt="Main" className="review-main-image" />
                                ) : (
                                    <div className="no-image">No photos added</div>
                                )}
                            </div>

                            <div className="review-details">
                                <h3>{formData.title || 'Untitled'}</h3>
                                <p className="review-price">£{parseFloat(formData.price || 0).toLocaleString()}</p>
                                <p className="review-meta">
                                    {categories.find(c => c.categoryId === parseInt(formData.categoryId))?.name || 'No category'} • {formData.condition} • {formData.location}
                                </p>
                                <p className="review-description">{formData.description}</p>
                                
                                {formData.requiresCertificate && (
                                    <div className="review-certificate">
                                        <Shield size={18} />
                                        <span>{formData.certificateType} Required - Collection Only</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="fee-info">
                            <h4>Platform Fee</h4>
                            <p>
                                When your item sells, a platform fee will be deducted from the sale (same fee logic for member and venue-posted listings):
                            </p>
                            <ul>
                                <li><strong>Premium Members:</strong> 2.5% fee</li>
                                <li><strong>Standard Members:</strong> 5% fee</li>
                            </ul>
                        </div>

                        <div className="terms-section">
                            <label className="checkbox-label terms-checkbox">
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                />
                                <span>
                                    I confirm that I have the legal right to sell this item and agree to the 
                                    <a href="/terms" target="_blank"> Terms of Service</a> and 
                                    <a href="/marketplace-terms" target="_blank"> Marketplace Guidelines</a>.
                                    {formData.requiresCertificate && (
                                        <span className="certificate-confirm">
                                            {' '}I understand that I must verify the buyer holds a valid {formData.certificateType} before any transfer.
                                        </span>
                                    )}
                                </span>
                            </label>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondary" onClick={prevStep}>
                                Back
                            </button>
                            <button 
                                type="submit" 
                                className="btn-primary"
                                disabled={isLoading || !agreedToTerms}
                            >
                                {isLoading ? 'Creating...' : 'Publish Listing'}
                            </button>
                        </div>
                    </div>
                )}
            </form>

            {/* Legal Footer */}
            <div className="legal-footer">
                <p>
                    By listing an item, you agree to comply with all applicable UK laws including the Firearms Act 1968.
                    Shoot Connect is a platform connecting buyers and sellers - we are not party to any transaction.
                </p>
            </div>
        </div>
    );
};

export default CreateListing;
